/**
 * ThinkingIndicator — Loading animation with progressive status messages.
 *
 * Shows the assistant icon + name header, a braille spinner, and a
 * typewriter status text with trailing dots that progresses through stages.
 * All animations use refs for DOM updates to avoid re-renders.
 */

import React, { useState, useEffect, useRef } from "react";
import { useTranslation } from "react-i18next";
import cogsolAvatar from "../assets/cogsol-logo.svg";

const DEFAULT_AVATAR = cogsolAvatar;

const STAGE_DELAYS = [15000, 30000, 50000];
const STAGE_KEYS = [
  "thinking",
  "analyzingRequest",
  "runningTasksToPrepareResponse",
  "thisMightTakeAWhile",
];
const BRAILLE_FRAMES = ["⠋", "⠙", "⠹", "⠸", "⠼", "⠴", "⠦", "⠧", "⠇", "⠏"];
const BRAILLE_SPEED = 80;
const TYPEWRITER_SPEED = 20;
const DOTS_SPEED = 500;

const ThinkingIndicator = ({ assistantColors, urlLogo, chatName }) => {
  const { t } = useTranslation();
  const [stage, setStage] = useState(0);
  const stageTimersRef = useRef([]);
  const brailleRef = useRef(null);
  const textRef = useRef(null);
  const dotsRef = useRef(null);

  // Stage transitions
  useEffect(() => {
    stageTimersRef.current = STAGE_DELAYS.map((delay, i) =>
      setTimeout(() => setStage(i + 1), delay),
    );
    return () => stageTimersRef.current.forEach(clearTimeout);
  }, []);

  // Braille spinner (ref-based, no re-renders)
  useEffect(() => {
    let frame = 0;
    const id = setInterval(() => {
      frame = (frame + 1) % BRAILLE_FRAMES.length;
      if (brailleRef.current) brailleRef.current.textContent = BRAILLE_FRAMES[frame];
    }, BRAILLE_SPEED);
    return () => clearInterval(id);
  }, []);

  // Typewriter + trailing dots (ref-based, no re-renders)
  useEffect(() => {
    const fullText = t(STAGE_KEYS[stage]);
    let charIndex = 0;
    let dotCount = 0;
    let dotsId = null;

    if (textRef.current) textRef.current.textContent = "";
    if (dotsRef.current) dotsRef.current.textContent = "";

    const typewriterId = setInterval(() => {
      charIndex++;
      if (charIndex <= fullText.length) {
        if (textRef.current) textRef.current.textContent = fullText.slice(0, charIndex);
      } else {
        clearInterval(typewriterId);
        dotsId = setInterval(() => {
          dotCount = (dotCount + 1) % 4;
          if (dotsRef.current) dotsRef.current.textContent = ".".repeat(dotCount);
        }, DOTS_SPEED);
      }
    }, TYPEWRITER_SPEED);

    return () => {
      clearInterval(typewriterId);
      if (dotsId) clearInterval(dotsId);
    };
  }, [stage, t]);

  return (
    <div className="flex flex-col gap-0.5 my-6">
      <div className="flex items-center gap-1.5 mb-1">
        <img
          src={urlLogo || DEFAULT_AVATAR}
          alt={chatName || t("assistant")}
          className="w-8 h-8 rounded-md object-cover shrink-0"
          onError={(e) => { e.target.src = DEFAULT_AVATAR; }}
        />
        <span
          className="text-xs font-semibold truncate max-w-[200px]"
          style={{ color: assistantColors?.nameColor || "#111827" }}
        >
          {chatName || t("assistant")}
        </span>
      </div>

      <div
        className="flex items-center gap-1.5 py-2 pl-0.5"
        role="status"
      >
        <span
          ref={brailleRef}
          className="text-sm font-mono leading-none shrink-0 text-gray-400"
          aria-hidden="true"
        >
          {BRAILLE_FRAMES[0]}
        </span>
        <span className="text-xs text-gray-400" data-testid="thinking-status">
          <span ref={textRef} />
          <span ref={dotsRef} />
        </span>
      </div>
    </div>
  );
};

export { ThinkingIndicator };
